import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";

// Common IANA timezones — enough for most users, more can be typed if we ever need it
const TIMEZONES = [
  "UTC",
  "Asia/Kolkata", "Asia/Dubai", "Asia/Singapore", "Asia/Tokyo", "Asia/Shanghai", "Asia/Hong_Kong",
  "Europe/London", "Europe/Paris", "Europe/Berlin", "Europe/Amsterdam", "Europe/Moscow",
  "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
  "America/Sao_Paulo", "America/Toronto", "America/Mexico_City",
  "Australia/Sydney", "Australia/Perth", "Pacific/Auckland",
  "Africa/Johannesburg", "Africa/Cairo",
];

export default function Profile() {
  const { user, refresh } = useAuth();
  const [prefs, setPrefs] = useState({ timezone: "UTC", digest_opt_out: false });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) setPrefs({ timezone: user.timezone || "UTC", digest_opt_out: !!user.digest_opt_out });
  }, [user]);

  const save = async (patch) => {
    setSaving(true);
    try {
      const { data } = await api.patch("/auth/me/preferences", patch);
      setPrefs({ timezone: data.timezone, digest_opt_out: data.digest_opt_out });
      if (refresh) await refresh();
      toast.success("Preferences saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const localExample = (() => {
    try {
      return new Intl.DateTimeFormat("en-US", {
        timeZone: prefs.timezone, weekday: "short", month: "short", day: "numeric",
        hour: "numeric", minute: "2-digit", timeZoneName: "short",
      }).format(new Date());
    } catch {
      return "—";
    }
  })();

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="max-w-2xl mx-auto px-6 py-8">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4">
          <ArrowLeft size={14} /> Back to dashboard
        </Link>
        <h1 className="text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
          Profile
        </h1>
        <p className="text-slate-500 mb-6">{user?.email} · {user?.role?.replace("_", " ")}</p>

        <Card className="p-5 rounded-md border-slate-200 shadow-sm space-y-5" data-testid="profile-card">
          <div className="space-y-2">
            <Label>Timezone</Label>
            <Select value={prefs.timezone} onValueChange={(v) => save({ timezone: v })} disabled={saving}>
              <SelectTrigger data-testid="tz-select" className="h-10"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                {TIMEZONES.map((tz) => <SelectItem key={tz} value={tz}>{tz}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500">Your reminder and digest emails will format deadlines in this timezone. Right now: <span className="text-slate-800 font-medium">{localExample}</span></p>
          </div>

          {(user?.role === "master_admin" || user?.role === "sub_admin") && (
            <div className="flex items-start justify-between gap-4 pt-3 border-t border-slate-100">
              <div className="flex-1">
                <Label className="text-sm">Weekly Monday digest</Label>
                <p className="text-xs text-slate-500 mt-1">
                  {prefs.digest_opt_out
                    ? "Off — you will NOT receive the Monday morning digest."
                    : "On — you'll receive a Monday 09:00 digest with open and overdue tasks grouped by assignee."}
                </p>
              </div>
              <Switch
                checked={!prefs.digest_opt_out}
                onCheckedChange={(v) => save({ digest_opt_out: !v })}
                disabled={saving}
                data-testid="digest-toggle"
              />
            </div>
          )}
        </Card>
      </main>
    </div>
  );
}
